import React, { useState } from "react";
import { toast } from "sonner";
import { PackageSearch, Check, Truck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import StoreShell from "@/components/StoreShell";
import { hexA } from "@/lib/storeutils";
import { PageTitle } from "./Shop";
import { Splash, ErrorView } from "./Storefront";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const STEPS = ["pending", "confirmed", "processing", "shipped", "out_for_delivery", "delivered"];

// Rendered inside the store's themed layout, so it shares the standard header menu and footer with every other page.
export default function Track() {
  const s = useStore();
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/store/${s.slug}/track`, { params: { order_number: orderNumber, email } });
      setOrder(data);
    } catch (err) { setOrder(null); toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  const { c, font, border, muted, cardShadow } = s;
  const stepIndex = order ? STEPS.indexOf(order.status) : -1;
  const input = "mt-1.5 w-full rounded-xl px-4 py-3 outline-none border transition-colors";
  const inputStyle = { background: c.bg, borderColor: border, color: c.text };
  const label = "text-xs font-semibold uppercase tracking-wider";

  return (
    <StoreShell>
      <PageTitle s={s} title="Track your order" subtitle="Enter your order number and the email you used at checkout." />
      <div className="max-w-2xl mx-auto px-5 py-10">
        <form onSubmit={track} className="rounded-2xl p-6 grid sm:grid-cols-2 gap-4" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }}>
          <label className="block">
            <span className={label} style={{ color: muted }}>Order number</span>
            <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value.toUpperCase())} placeholder="S24-XXXXXX" data-testid="track-order-number" className={`${input} font-mono`} style={inputStyle} />
          </label>
          <label className="block">
            <span className={label} style={{ color: muted }}>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="track-email" className={input} style={inputStyle} />
          </label>
          <button type="submit" disabled={loading} data-testid="track-submit" className="sm:col-span-2 py-3 rounded-full font-bold transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: c.accent, color: "#fff" }}>
            <PackageSearch size={17} /> {loading ? "Searching…" : "Track order"}
          </button>
        </form>

        {order && (
          <div className="mt-6 rounded-2xl p-6" style={{ background: c.surface, border: `1px solid ${border}`, boxShadow: cardShadow }} data-testid="track-result">
            <div className="flex items-center justify-between">
              <div className="font-mono font-bold" style={{ color: c.accent }}>{order.order_number}</div>
              <div className="font-bold" style={{ color: c.primary }}>{inr(order.total)}</div>
            </div>
            <div className="text-xs mt-1" style={{ color: muted }}>Placed {(order.created_at || "").slice(0, 10)} · {order.fulfillment_type}</div>

            {stepIndex >= 0 ? (
              <div className="mt-6 flex items-center">
                {STEPS.map((st, i) => (
                  <React.Fragment key={st}>
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={i <= stepIndex ? { background: c.primary, color: "#fff" } : { background: c.bg, color: muted, border: `1px solid ${border}` }}>
                        {i <= stepIndex ? <Check size={15} /> : <Truck size={13} />}
                      </div>
                      <span className="text-[9px] mt-1 capitalize text-center w-14" style={{ color: muted }}>{st.replace(/_/g, " ")}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className="flex-1 h-0.5" style={{ background: i < stepIndex ? c.primary : border }} />}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="mt-4 text-sm"><span className="px-3 py-1 rounded-full capitalize" style={{ background: hexA(c.accent, 0.15), color: c.accent }}>{order.status.replace(/_/g, " ")}</span></div>
            )}

            <div className="mt-6 pt-4 space-y-2" style={{ borderTop: `1px solid ${border}` }}>
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm"><span style={{ fontFamily: font }}>{it.name} × {it.quantity}</span><span>{inr(it.price * it.quantity)}</span></div>
              ))}
            </div>
          </div>
        )}
      </div>
    </StoreShell>
  );
}
